import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Card, Typography, TextField, Button, Alert, Stack, CircularProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

// Página pública del link "Restablece tu PIN" (llega por email, expira en 1h)
export default function ResetPinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onlyDigits = (v) => v.replace(/[^0-9]/g, '').slice(0, 4);

  const handleSubmit = async () => {
    setError('');
    if (pin.length !== 4) { setError('El PIN debe ser de 4 dígitos.'); return; }
    if (pin !== pinConfirm) { setError('Los PINs no coinciden.'); return; }
    setLoading(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/resetPinWithToken`, { token, pin });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo restablecer el PIN. Intenta de nuevo.');
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet><title>Restablecer PIN | B&B Target Center</title></Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ p: 4, borderRadius: 3, maxWidth: 420, width: '100%', textAlign: 'center' }}>
          {done ? (
            <Stack spacing={2} alignItems="center">
              <CheckCircleIcon sx={{ fontSize: 56, color: '#28a745' }} />
              <Typography variant="h5" fontWeight="bold">¡PIN actualizado!</Typography>
              <Typography variant="body2" color="text.secondary">
                Ya puedes usar tu nuevo PIN para reservar en la web y entrar al app.
              </Typography>
              <Button variant="contained" fullWidth size="large" onClick={() => navigate('/make-reservation')}>
                Ir a reservar
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Box>
                <LockIcon sx={{ fontSize: 44, color: '#d40100', mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">Escoge tu nuevo PIN</Typography>
                <Typography variant="body2" color="text.secondary">
                  4 dígitos — lo usarás para reservar y entrar al app.
                </Typography>
              </Box>

              {!token && (
                <Alert severity="error">
                  Este link no es válido. Abre el link completo que te llegó por email.
                </Alert>
              )}
              {error && <Alert severity="error">{error}</Alert>}

              <TextField
                label="Nuevo PIN"
                type="password"
                value={pin}
                onChange={(e) => setPin(onlyDigits(e.target.value))}
                inputProps={{ inputMode: 'numeric', maxLength: 4, style: { textAlign: 'center', letterSpacing: 8, fontSize: 22 } }}
                disabled={!token}
              />
              <TextField
                label="Confirma tu PIN"
                type="password"
                value={pinConfirm}
                onChange={(e) => setPinConfirm(onlyDigits(e.target.value))}
                inputProps={{ inputMode: 'numeric', maxLength: 4, style: { textAlign: 'center', letterSpacing: 8, fontSize: 22 } }}
                disabled={!token}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!token || loading || pin.length !== 4 || pinConfirm.length !== 4}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {loading ? 'Guardando...' : 'Guardar PIN'}
              </Button>
            </Stack>
          )}
        </Card>
      </Box>
    </>
  );
}
